import { useEffect, useRef } from 'react'

/**
 * Custom hook for managing spot mode lifecycle effects
 * Handles map view saving/restoring, centering, and entity pre-selection
 */
export function useSpotModeEffects({
  isSpotModeActive,
  userLocation,
  preSelectedEntity,
  spotModeEntity,
  initialLocation,
  spotModeLocation,
  isFullscreenMode,
  isLoading,
  mapRef,
  savedMapViewRef,
  currentOpenPopupRef,
  setSpotModeEntity,
  setEntitySearchQuery,
  setUserLocation,
  setSpotModeLocation,
  setSpotModeNotes,
  setSpotModeEntityStatus,
  setIsSpotPanelMinimized,
  getUserLocation
}) {
  // Auto-activate spot mode when in fullscreen mode
  useEffect(() => {
    if (isFullscreenMode && !isSpotModeActive) {
      // Small delay to ensure map is mounted
      const timer = setTimeout(() => {
        // Spot mode is already active from context, just ensure location can be set
        // Try to get user's location if we don't have it yet
        if (!userLocation && !isLoading) {
          getUserLocation()
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isFullscreenMode, isSpotModeActive, userLocation, isLoading, getUserLocation])

  // Handle spot mode activation
  useEffect(() => {
    if (isSpotModeActive) {
      // Close all open popups when spot mode is activated
      if (mapRef.current) {
        mapRef.current.closePopup()
      }
      if (currentOpenPopupRef?.current) {
        try {
          currentOpenPopupRef.current.closePopup()
        } catch (e) {
          // Popup may already be closed
        }
        currentOpenPopupRef.current = null
      }
      
      // Save current map view before changing it
      if (mapRef.current && !savedMapViewRef.current) {
        const map = mapRef.current
        // Use whenReady to ensure map is fully initialized
        map.whenReady(() => {
          try {
            // Check if container still exists in DOM before accessing
            if (!map.getContainer() || !document.body.contains(map.getContainer())) {
              return
            }
            savedMapViewRef.current = {
              center: map.getCenter(),
              zoom: map.getZoom()
            }
          } catch (error) {
            // Silently ignore - map might be unmounting
          }
        })
      }

      // Only recenter if no marker has been dropped yet
      if (mapRef.current && !spotModeLocation) {
        const map = mapRef.current
        // Use whenReady to ensure map is fully initialized before operations
        map.whenReady(() => {
          try {
            // Check if container still exists in DOM before accessing
            if (!map.getContainer() || !document.body.contains(map.getContainer())) {
              return
            }
            // Priority: 1) initialLocation (from entity), 2) userLocation, 3) current center
            if (initialLocation) {
              map.setView([initialLocation.lat, initialLocation.lng], 17, {
                animate: false
              })
              // Auto-place pin at the pre-selected location
              setSpotModeLocation({ lat: initialLocation.lat, lng: initialLocation.lng })
            } else if (userLocation) {
              map.setView([userLocation.latitude, userLocation.longitude], 17, {
                animate: false
              })
              // Auto-place pin at user's current GPS position
              setSpotModeLocation({ lat: userLocation.latitude, lng: userLocation.longitude })
            } else {
              // GPS not yet resolved — request it now so the reactive re-run auto-places
              // the pin once getCurrentPosition callback fires
              getUserLocation()
              // Zoom to street level at current center while we wait
              const currentCenter = map.getCenter()
              map.setView(currentCenter, 17, {
                animate: false
              })
            }
          } catch (error) {
            // Silently ignore - map might be unmounting
          }
        })
      }

      // If there's a pre-selected entity, use it and load its sightings
      if (preSelectedEntity && !spotModeEntity) {
        setSpotModeEntity(preSelectedEntity)
        setEntitySearchQuery(preSelectedEntity.name)

        // Don't refetch entity locations when entering spot mode - keep existing markers
        // Only center the map if an initial location is provided
        if (initialLocation) {
          // Just center the map without loading entity sightings
          // initialLocation uses { lat, lng } Leaflet convention (matches map.setView usage above)
          setUserLocation({
            latitude: initialLocation.lat,
            longitude: initialLocation.lng
          })
        }
      }
    } else {
      // Restore previous map view when exiting spot mode
      if (mapRef.current && savedMapViewRef.current) {
        const map = mapRef.current
        // Use whenReady for safety
        map.whenReady(() => {
          try {
            // Check if container still exists in DOM before accessing
            if (!map.getContainer() || !document.body.contains(map.getContainer())) {
              return
            }
            // Stop any in-flight zoom animation before restoring view.
            // Without this, Leaflet's _onZoomTransitionEnd fires after React
            // has replaced the map pane, crashing on _leaflet_pos read.
            map.stop()
            map.setView(savedMapViewRef.current.center, savedMapViewRef.current.zoom, {
              animate: false
            })
          } catch (error) {
            // Silently ignore - map might be unmounting
          }
        })
        savedMapViewRef.current = null // Clear saved view
      }

      // Clean up spot mode state when deactivated
      setSpotModeLocation(null)
      setSpotModeEntity(null)
      setSpotModeNotes('')
      if (setSpotModeEntityStatus) setSpotModeEntityStatus(null)
      setIsSpotPanelMinimized(false) // Reset minimized state
    }
  }, [
    isSpotModeActive,
    userLocation,
    preSelectedEntity,
    spotModeEntity,
    initialLocation,
    spotModeLocation,
    mapRef,
    savedMapViewRef,
    setSpotModeEntity,
    setEntitySearchQuery,
    setUserLocation,
    setSpotModeLocation,
    setSpotModeNotes,
    setSpotModeEntityStatus,
    setIsSpotPanelMinimized,
    getUserLocation
  ])
}
